import {
    lazyPreviewComponentImport,
    LogLevel,
    useLogger,
} from '@amzn/event-engine-js-utils';
import { state as breadcrumbsState } from 'atoms/breadcrumbs';
import { state as contentTypeState } from 'atoms/content-type';
import { state as maxContentWidthState } from 'atoms/max-content-width';
import { state as navigationContentState } from 'atoms/navigation-content';
import ErrorBoundaryWithLogger from 'components/ErrorBoundaryWithLogger';
import ErrorPageController from 'components/ErrorPageController';
import LoadingComponent from 'components/LoadingComponent';
import useEmbeddedBuild from 'hooks/use-embedded-build';
import React, { lazy, Suspense, useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import EEContentCatalog from 'services/ee-content-catalog';

const LazyPreviewComponent = lazy(lazyPreviewComponentImport);

export interface PublishedConfig {
    current: string;
    previous: string[];
}

const EmbeddedBuild = () => {
    const {
        loading,
        error,
        publishedContentUrl,
        kinesisFirehoseStreamLocale,
        requiresAuth,
        contentBuildId,
    } = useEmbeddedBuild();
    const [previewLogger] = useLogger(
        'EmbeddedBuildLogger',
        process.env.NODE_ENV === 'development'
            ? LogLevel.VERBOSE
            : LogLevel.ERROR
    );
    const setNavigationContent = useSetRecoilState(navigationContentState);
    const getContentRequest = useCallback(
        () =>
            EEContentCatalog.getContentBuild({
                contentBuildId: contentBuildId!,
            }).then(({ contentBuild }) => ({
                artifact: contentBuild!.artifact,
                contentId: contentBuild!.contentId,
                contentBuildId: contentBuild!.contentBuildId,
            })),
        [contentBuildId]
    );
    const onUnmount = useCallback(() => {
        setNavigationContent(null);
    }, []);

    if (error) {
        return <ErrorPageController type="404" />;
    }

    if (loading) {
        return <LoadingComponent containerProps={{ margin: 'm' }} />;
    }

    return (
        <ErrorBoundaryWithLogger loggerName="EmbeddedBuildErrorBoundaryLogger">
            {!!(publishedContentUrl && contentBuildId) && (
                <Suspense
                    fallback={
                        <LoadingComponent containerProps={{ margin: 'm' }} />
                    }>
                    <LazyPreviewComponent
                        logger={previewLogger}
                        getContentArtifact={
                            requiresAuth ? getContentRequest : undefined
                        }
                        publicContentUrl={publishedContentUrl}
                        contentBuildId={contentBuildId}
                        defaultAppNav={null}
                        navigationState={navigationContentState}
                        breadcrumbsState={breadcrumbsState}
                        maxContentWidthState={maxContentWidthState}
                        contentTypeState={contentTypeState}
                        onUnmount={onUnmount}
                        kinesisFirehoseStreamLocale={
                            kinesisFirehoseStreamLocale
                        }
                        defaultAppTitleName={document.title}
                    />
                </Suspense>
            )}
        </ErrorBoundaryWithLogger>
    );
};

export default EmbeddedBuild;
